import React, { useState } from 'react';
import { Faq } from '../components/Faq';
import { Ledger } from '../components/Ledger';
import { faqsContact } from '../data/faqsContact';
import { CONTACT_EMAIL, DEMO_URL, FORM_ENDPOINT, SIGN_IN_URL } from '../data/links';
const VERTICALS = ['Pharma', 'Finance', 'Insurance', 'Legal', 'Other'];
const OWNERSHIP = [{
  n: '01',
  label: 'Your library',
  body: 'It starts from a governed baseline — regulatory, brand, role — and becomes proprietary the first time your best reviewer’s correction gets atomized. Generic goes in once. What comes back is yours, and it never returns to the marketplace.'
}, {
  n: '02',
  label: 'Your environment',
  body: 'The library lives where your data lives. Your policies, your transcripts, your corrections stay inside your walls. Nothing leaves. Nothing trains anyone else’s model.'
}, {
  n: '03',
  label: 'Your leverage',
  body: 'Instruction data is portable: readable by your compliance officer, compilable for any model. Switch models Tuesday. The library doesn’t notice.'
}];
const NEXT = [{
  n: '01',
  title: 'We compile your workflow.',
  body: 'Your obligations, seated and fired, on the request you named.'
}, {
  n: '02',
  title: 'You see the receipt.',
  body: 'Which rules rode, which versions, on what trigger — signed.'
}, {
  n: '03',
  title: 'A 30-minute walkthrough.',
  body: 'Where it fits, and where it doesn’t. If it’s not for you, we’ll say so.'
}];
export function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [vertical, setVertical] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error' | 'mail'>('idle');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const fields = {
      name,
      email,
      vertical: vertical ?? '—',
      workflow,
      // Identifies the sender in a busy inbox rather than every submission
      // arriving under one identical subject line.
      _subject: `Synthia contact — ${trimmedName || 'no name'}${vertical ? ` (${vertical})` : ''}`
    };

    // No endpoint configured yet: hand off to the visitor's mail client so the
    // message still has somewhere to go.
    if (!FORM_ENDPOINT) {
      const body = [`Name: ${name}`, `Email: ${email}`, `Vertical: ${fields.vertical}`, '', `Workflow: ${workflow}`].join('\n');
      setStatus('mail');
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(fields._subject)}&body=${encodeURIComponent(body)}`;
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(fields)
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('sent');
      setName('');
      setEmail('');
      setVertical(null);
      setWorkflow('');
    } catch {
      setStatus('error');
    }
  };
  return <>
      <header className="hero contact-hero" data-id="element-374" data-mp="2hia472u71kwo">
        {/* Full-width hero: no .col-l, whose divider would double the frame's
            own right rule. */}
        <div style={{
        width: '100%'
      }} data-id="element-375" data-mp="3a4uvk3ao5x08">
          <div className="lab-row" data-id="element-376" data-mp="3a5xki5oflpdi">
            <span className="clabel" data-id="element-377" data-mp="3f3z5wx9by0s5">Start here</span>
          </div>
          <div className="head-block" data-id="element-378" data-mp="3f24tk7c4jgzo">
            <h1 style={{
            maxWidth: 'none',
            fontSize: 'clamp(26px,3.7vw,46px)'
          }} data-id="element-379" data-mp="314zhnqpqbnwi">
              Bring your <span className="g" data-id="element-380" data-mp="355rtmrpzk6wh">highest standards.</span>
            </h1>
          </div>
          <div className="sub-block" data-id="element-381" data-mp="1ppj6enwv3o5b">
            <p className="sub" data-id="element-382" data-mp="1pohukh80uxmj">
              Tell us the workflow where being wrong costs the most. That&rsquo;s the one we want.
            </p>

            <form className="form" onSubmit={submit} data-id="element-383" data-mp="1tojp86fcaqsb">
              <div className="field" data-id="element-384" data-mp="29ysr7g8659ff">
                <label htmlFor="cf-name" data-id="element-385" data-mp="29zu65vsb2g2f">Name</label>
                <input id="cf-name" type="text" required value={name} onChange={e => setName(e.target.value)} data-id="element-386" data-mp="2569cgfxqw891" />
              </div>
              <div className="field" data-id="element-387" data-mp="2064x4t8kpjke">
                <label htmlFor="cf-email" data-id="element-388" data-mp="1v9mv414mnzbr">Work email</label>
                <input id="cf-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-id="element-389" data-mp="1v71rp7oolbbw" />
              </div>
              <div className="field" data-id="element-390" data-mp="2iy60o8waly05">
                <span className="cf-label" id="cf-vertical-label" data-id="element-391" data-mp="2ix4ilkgv0b8x">
                  Vertical
                </span>
                <div className="chips" role="group" aria-labelledby="cf-vertical-label" data-id="element-392" data-mp="2ez6p45g232zq">
                  {VERTICALS.map(v => <button type="button" key={v} className={`chip${vertical === v ? ' sel' : ''}`} aria-pressed={vertical === v} onClick={() => setVertical(v)} data-id="element-393" data-mp="13hff92wy4qyf">
                      {v}
                    </button>)}
                </div>
              </div>
              <div className="field" data-id="element-394" data-mp="3swysqs85odg2">
                <label htmlFor="cf-workflow" data-id="element-395" data-mp="3sy04kywzx3yu">The workflow</label>
                <textarea id="cf-workflow" required value={workflow} onChange={e => setWorkflow(e.target.value)} placeholder="I have to respond to compliance findings, and every draft comes back with something missing…" data-id="element-396" data-mp="1r745xa118vd" />
              </div>
              <div data-id="element-397" data-mp="fo2pop7gsssb">
                <button className="btn g" type="submit" disabled={status === 'sending'} data-id="element-398" data-mp="2cigtgbrcptgo">
                  {status === 'sending' ? 'Sending…' : 'Send message →'}
                </button>
              </div>
              {/* aria-live region: one node that stays mounted, so each outcome is
                  announced rather than silently swapping the form's tail. */}
              <p className="cf-sent" role="status" aria-live="polite" data-id="element-399" data-mp="3fv7ehz32qvon">
                {status === 'sent' ? <span className="cf-ok" data-id="element-400" data-mp="3tqrjdqlyxdd2">
                    Message sent. We read every one &mdash; expect a reply within a business day.
                  </span> : null}
                {status === 'error' ? <span className="cf-err" data-id="element-401" data-mp="2ncog62ym7l9">
                    That didn&rsquo;t go through. Write to{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} data-id="element-402" data-mp="gehgam035vl1">{CONTACT_EMAIL}</a> and we&rsquo;ll pick it up there.
                  </span> : null}
                {status === 'mail' ? <>
                    Your message is queued in your mail app. If nothing opened, write to{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} data-id="element-403" data-mp="lenaa8ttvu6a">{CONTACT_EMAIL}</a> directly.
                  </> : null}
              </p>
            </form>
          </div>
        </div>
      </header>

      <div className="split" id="demo" data-id="element-404" data-mp="19wvzgfc1w9sz">
        <div className="sp-l" data-id="element-405" data-mp="v0yb3j8dc9rb">
          <span className="clabel" data-id="element-406" data-mp="v1ptfscygazn">Book a demo</span>
          <h2 data-id="element-407" data-mp="v311i4dfo4wk">Or skip the form.</h2>
          <p className="lede" data-id="element-408" data-mp="zzhw9hf51him">
            Thirty minutes on a workflow you actually care about. We compile it, you read the receipt,
            and if it isn&rsquo;t a fit we&rsquo;ll tell you.
          </p>
          <div className="ctas" style={{
          marginTop: 26
        }} data-id="element-409" data-mp="qzl1cy25nmf4">
            <a className="btn g" href={DEMO_URL} target="_blank" rel="noopener" data-id="element-410" data-mp="r161d378nxkd">
              Schedule a demo &rarr;
            </a>
          </div>
        </div>
        <div className="sp-r" data-id="element-411" data-mp="ha1k1wu3c0md">
          <div style={{
          width: '100%'
        }} data-id="element-412" data-mp="h76z84760dsf">
            <div className="reach" data-id="element-413" data-mp="39ieqxh0f8nw">
              <div className="reachr" data-id="element-414" data-mp="3ak0pwzkdfke">
                <span className="rl" data-id="element-415" data-mp="7b3y91o1ul72">Email us here</span>
                <a className="rv" href="mailto:hello@meetsynthia.ai" data-id="element-416" data-mp="7akedfeu9mqe">
                  hello@meetsynthia.ai
                </a>
              </div>
              <div className="reachr" data-id="element-417" data-mp="ir8m98973vjl">
                <span className="rl" data-id="element-418" data-mp="isb9953eddu8">Talk to sales</span>
                <a className="rv" href="mailto:sales@meetsynthia.ai" data-id="element-419" data-mp="ds5q6db70znr">
                  sales@meetsynthia.ai
                </a>
              </div>
              <div className="reachr" data-id="element-420" data-mp="8tcbihk672m0">
                <span className="rl" data-id="element-421" data-mp="4rqs0dd4rq2u">Already a customer</span>
                <a className="rv" href={SIGN_IN_URL} target="_top" rel="noopener" data-id="element-422" data-mp="4tbo47f3fh2t">
                  app.meetsynthia.ai
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid3" data-id="element-423" data-mp="3r58slxtpg4nw">
        {OWNERSHIP.map(o => <div className="cell" key={o.n} data-id="element-424" data-mp="3m9tkznmaj4zx">
            <div className="lab" data-id="element-425" data-mp="3h9e5apejdles">
              <span data-id="element-426" data-mp="3h9np1v8zmwc1">{o.label}</span>
              <span className="n" data-id="element-427" data-mp="3h8m7d7xgvblc">{o.n}</span>
            </div>
            <p className="lede" data-id="element-428" data-mp="3d8vbipt3259y">{o.body}</p>
          </div>)}
      </div>

      <div className="split" data-id="element-429" data-mp="1sqb0t56wa00g">
        <div className="sp-l" data-id="element-430" data-mp="1sozzrd4u36d1">
          <p className="kicker" style={{
          marginTop: 0
        }} data-id="element-431" data-mp="1srui8z4aeh3t">
            Rent the model.
            <br data-id="element-432" data-mp="1wpjwiflhw7yn" />
            Own the instructions.
          </p>
        </div>
        <div className="sp-r" data-id="element-433" data-mp="21rrble1mg457">
          <div style={{
          width: '100%'
        }} data-id="element-434" data-mp="21od7o1db7mvn">
            <span className="clabel" data-id="element-435" data-mp="26psvu2asa9i9">What happens next</span>
            <div className="steps" style={{
            marginTop: 20
          }} data-id="element-436" data-mp="26n6pu73jk225">
              {NEXT.map(s => <div className="stepr" key={s.n} data-id="element-437" data-mp="18zxvrnph5koj">
                  <span className="sn" data-id="element-438" data-mp="1e1wzwzt1glmj">{s.n}</span>
                  <span className="st" data-id="element-439" data-mp="1dyivlm0te2cg">
                    <b data-id="element-440" data-mp="1izovc3sw6pvh">{s.title}</b> {s.body}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      <div className="stoprow" data-id="element-441" data-mp="qbatte2lhbf6">
        <h2 data-id="element-442" data-mp="qc2p0nsa574x">
          Asked and <span className="g" data-id="element-443" data-mp="qarmssehg7fx">answered.</span>
        </h2>
        <span className="clabel" data-id="element-444" data-mp="v9ugjgp0ir8l">FAQ</span>
      </div>

      <Faq items={faqsContact} data-id="element-445" data-mp="v8jfhomybxl6" />

      <Ledger data-id="element-446" data-mp="oxufp92qte6i" />
    </>;
}